// hooks/useCreatorsHub.ts — reactive hook for the Creators' Hub.
//
// Reads from creatorsHubStore reactively and exposes service mutations.
// Components must NOT import creatorsHubStore or creatorsHubService directly.
//
// useShallow is used on any filtering/derived-list selector to prevent
// re-renders when unrelated hub state changes.

import { useShallow } from 'zustand/react/shallow';
import { useCreatorsHubStore } from '@/core/stores/creatorsHubStore';
import { creatorsHubService } from '@/core/services/creatorsHubService';
import type { HubDiscussion, HubCollaboration, HubInvite, HubEvent } from '@/core/models/creators-hub';

export interface CreatorsHubHook {
  /** All discussions in seed order. */
  discussions: HubDiscussion[];
  /** All collaborations. */
  collaborations: HubCollaboration[];
  /** All pending invites. */
  invites: HubInvite[];
  /** All upcoming events. */
  events: HubEvent[];
  /** Featured creator handles. */
  featuredCreatorHandles: string[];
  /** Toggle interest on a collaboration by id. */
  toggleInterest: (id: string) => Promise<void>;
  /** Accept a creator invite. */
  acceptInvite: (id: string) => Promise<void>;
  /** Decline a creator invite. */
  declineInvite: (id: string) => Promise<void>;
}

export interface CollaborationsHook {
  /** Only collaborations where youInterested === true. */
  collaborations: HubCollaboration[];
}

/**
 * Primary hook: all hub data + mutations.
 *
 * Note on useShallow:
 * - discussions, collaborations, invites, events: stable store refs (arrays)
 *   returned directly from the store. They are re-assigned via set() on mutation,
 *   so Zustand's reference equality check triggers re-renders on actual changes.
 *   No useShallow needed for these top-level arrays.
 * - featuredCreatorHandles: stable store ref.
 */
export function useCreatorsHub(): CreatorsHubHook {
  const discussions = useCreatorsHubStore((s) => s.discussions);
  const collaborations = useCreatorsHubStore((s) => s.collaborations);
  const invites = useCreatorsHubStore((s) => s.invites);
  const events = useCreatorsHubStore((s) => s.events);
  const featuredCreatorHandles = useCreatorsHubStore((s) => s.featuredCreatorHandles);

  return {
    discussions,
    collaborations,
    invites,
    events,
    featuredCreatorHandles,
    toggleInterest: (id) => creatorsHubService.toggleInterest(id),
    acceptInvite: (id) => creatorsHubService.acceptInvite(id),
    declineInvite: (id) => creatorsHubService.declineInvite(id),
  };
}

/**
 * Filtered hook: collaborations you have shown interest in.
 * Uses useShallow because the selector returns a filtered array (new ref each eval).
 */
export function useInterestedCollaborations(): CollaborationsHook {
  const collaborations = useCreatorsHubStore(
    useShallow((s) => s.collaborations.filter((c) => c.youInterested)),
  );
  return { collaborations };
}
